import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'

interface ContactPayload {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  company?: string
  position?: string
  address?: string
  category?: number | string
  [key: string]: any
}

interface ContactCategoryPayload {
  name: string
  color?: string
  [key: string]: any
}

interface ContactActionPayload {
  action: string
  contact_ids: (number | string)[]
  [key: string]: any
}

interface CompanySearchPayload {
  company_name: string
  country?: string
  [key: string]: any
}

/**
 * Updates a contact
 * @param id - Contact ID
 * @param payload - Contact data to update
 * @returns API response
 */
export const updateContact = (
  id: number | string,
  payload: Partial<ContactPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.CONTACTS.PATCH_CONTACTS_PK(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates a contact with file upload
 * @param id - Contact ID
 * @param payload - Contact data to update
 * @param headers - Request headers for file upload
 * @returns API response
 */
export const updateContactWithFile = (
  id: number | string,
  payload: Partial<ContactPayload>,
  headers: Record<string, string>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.CONTACTS.PATCH_CONTACTS_PK(id), payload, headers)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Creates a new contact
 * @param payload - Contact data
 * @returns API response
 */
export const addContact = (
  payload: ContactPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.CONTACTS.POST_CONTACTS_LIST_PK(), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Creates a new contact with file upload
 * @param org_pk - Organization ID
 * @param payload - Contact data
 * @param headers - Request headers for file upload
 * @returns API response
 */
export const addContactWithFile = (
  org_pk: number | string,
  payload: ContactPayload,
  headers: Record<string, string>
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.CONTACTS.POST_CONTACTS_LIST_PK(), payload, headers)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a contact
 * @param id - Contact ID
 * @returns API response
 */
export const deleteContact = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.CONTACTS.DELETE_CONTACT(id))
    .then(response => response)
    .catch(error => error.response)
}

/* Categories */

/**
 * Creates a new contact category
 * @param org_pk - Organization ID
 * @param payload - Category data
 * @returns API response
 */
export const addContactCategory = (
  org_pk: number | string,
  payload: ContactCategoryPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.CONTACTS.POST_CONTACTS_CATEGORIES_LIST(org_pk), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates a contact category
 * @param id - Category ID
 * @param payload - Category data to update
 * @returns API response
 */
export const updateContactCategory = (
  id: number | string,
  payload: Partial<ContactCategoryPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.CONTACTS.PATCH_CONTACTS_CATEGORY(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a contact category
 * @param id - Category ID
 * @returns API response
 */
export const deleteContactCategory = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.CONTACTS.DELETE_CONTACTS_CATEGORY(id))
    .then(response => response)
    .catch(error => error.response)
}

/* Actions */

/**
 * Performs a bulk action on contacts
 * @param org_pk - Organization ID
 * @param payload - Action data
 * @returns API response
 */
export const postContactAction = (
  org_pk: number | string,
  payload: ContactActionPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.CONTACTS.POST_CONTACTS_LIST_ACTION(org_pk), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Searches for a company
 * @param payload - Search criteria
 * @returns API response with search results
 */
export const onSearchCompany = (
  payload: CompanySearchPayload
): Promise<ApiClientResult> => {
  return client
    .post('/contacts/company_search/', {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}
